/**
 * RadarLayout Component
 *
 * Shared layout for all radar routes.
 * Provides:
 * - Header with title and search trigger
 * - Period selector
 * - Global search modal (⌘K)
 * - Last updated timestamp
 */
import React from 'react'
import { Link } from '@tanstack/react-router'
import { Search, RefreshCw, Calendar, ChevronDown, Target } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRadarUIStore } from '@/stores/radar-ui-store'
import { useInvalidateRadarCache } from '@/hooks/queries/useRadarQueries'
import { RadarSearch } from './RadarSearch'
import type { PeriodPreset } from '@/types/radar'

interface RadarLayoutProps {
  children: React.ReactNode
}

const periodOptions: { value: PeriodPreset; label: string }[] = [
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '60d', label: 'Últimos 60 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'custom', label: 'Personalizado' },
]

export function RadarLayout({ children }: RadarLayoutProps) {
  const { periodPreset, setPeriodPreset, openSearch } = useRadarUIStore()
  const invalidateCache = useInvalidateRadarCache()
  const [isPeriodOpen, setIsPeriodOpen] = React.useState(false)
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    invalidateCache()
    // Brief delay for visual feedback
    setTimeout(() => setIsRefreshing(false), 500)
  }

  const selectedPeriodLabel = periodOptions.find((p) => p.value === periodPreset)?.label || 'Últimos 90 dias'

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-brand-dark border-b border-brand-gray/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Title */}
            <div className="flex items-center gap-4">
              {/* Hidden: back link - feature temporarily disabled
              <Link
                to="/performance"
                className="text-brand-gray hover:text-white transition-colors"
              >
                ◀ Performance
              </Link>
              */}
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-brand-primary" />
                <h1 className="text-xl font-semibold text-white">Radar de Desempenho</h1>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Search trigger */}
              <button
                onClick={openSearch}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg',
                  'bg-brand-dark-light border border-brand-gray/30',
                  'text-brand-gray hover:text-white hover:border-brand-gray/50',
                  'transition-colors'
                )}
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Buscar</span>
                <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-brand-dark rounded">
                  <span>⌘</span>
                  <span>K</span>
                </kbd>
              </button>

              {/* Hidden: Period selector - feature temporarily disabled
              <div className="relative">
                <button
                  onClick={() => setIsPeriodOpen(!isPeriodOpen)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-lg',
                    'bg-brand-dark-light border border-brand-gray/30',
                    'text-brand-gray hover:text-white hover:border-brand-gray/50',
                    'transition-colors'
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">{selectedPeriodLabel}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {isPeriodOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsPeriodOpen(false)}
                    />
                    <div className="absolute right-0 mt-1 w-48 bg-brand-dark border border-brand-gray/30 rounded-lg shadow-xl z-20 overflow-hidden">
                      {periodOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setPeriodPreset(option.value)
                            setIsPeriodOpen(false)
                          }}
                          className={cn(
                            'w-full px-4 py-2 text-left text-sm transition-colors',
                            periodPreset === option.value
                              ? 'bg-brand-primary/10 text-brand-primary'
                              : 'text-brand-gray hover:bg-brand-dark-light hover:text-white'
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              */}

              {/* Hidden: Refresh button - feature temporarily disabled
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={cn(
                  'p-2 rounded-lg',
                  'bg-brand-dark-light border border-brand-gray/30',
                  'text-brand-gray hover:text-white hover:border-brand-gray/50',
                  'transition-colors disabled:opacity-50'
                )}
                title="Atualizar dados"
              >
                <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
              </button>
              */}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </div>

      {/* Global Search Modal */}
      <RadarSearch />
    </div>
  )
}
